import { useDateNow, useReduxState } from 'hooks';
import { getFormattedDate } from 'lib/common';
import { Avatar, EmptyIndicator } from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  AnswersProgress,
  ReadingProgress,
  TaskPointsProgress,
  VideoProgress
} from 'components/common/tasks';

import type { CourseProgress as CourseProgressData } from 'shared-types';
import type { GetUserCoursesCourseRes } from 'shared-types/responses';

interface Props {
  chapters: GetUserCoursesCourseRes['chapters'];
  languageVersion: string;
  progressData: CourseProgressData;
}

const ProfileCourseChapters = ({
  chapters,
  languageVersion,
  progressData
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages, theme } = useReduxState();

  return (
    <div className="chapters">
      <Box padding={0} title={messages.commonChapters}>
        {chapters && chapters.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell>{messages.commonChapter}</BoxTableCell>
                <BoxTableCell>{messages.tasksReading}</BoxTableCell>
                <BoxTableCell>{messages.tasksVideos}</BoxTableCell>
                <BoxTableCell>{messages.tasksAnswers}</BoxTableCell>
                <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                  {messages.tasksTaskPoints}
                </BoxTableCell>
              </BoxTableHeader>

              {chapters.map((chapter) => (
                <BoxTableRow key={chapter.id}>
                  <BoxTableCell>
                    <div className="chapter">
                      <div className="chapter-number">
                        <Avatar fontSize="1.4rem">{chapter.number}</Avatar>
                      </div>
                      <div className="chapter-title-wrapper">
                        <div className="chapter-title">
                          {chapter.titleLocales?.[languageVersion] ||
                            chapter.title}
                        </div>

                        {!chapter.publishDate ? (
                          <div className="unpublished">
                            {`${messages.commonNotPublished}`}
                          </div>
                        ) : Date.parse(chapter.publishDate as string) >
                          dateNow ? (
                          <div className="unpublished">
                            {`${messages.commonWillPublish} ${getFormattedDate({
                              date: chapter.publishDate,
                              dateFormat: 'dd.MM.y HH:mm'
                            })}`}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </BoxTableCell>

                  <BoxTableCell label={messages.tasksReading} shrink={true}>
                    <ReadingProgress
                      analyticsDisabledForAdmin={
                        progressData.analyticsDisabledForAdmin
                      }
                      readingProgress={
                        progressData.chapters[chapter.id].readingProgress
                      }
                      readingProgressMaxTime={
                        progressData.chapters[chapter.id].readingProgressMaxTime
                      }
                    />
                  </BoxTableCell>

                  <BoxTableCell label={messages.tasksVideos} shrink={true}>
                    <VideoProgress
                      analyticsDisabledForAdmin={
                        progressData.analyticsDisabledForAdmin
                      }
                      numberOfVideos={
                        progressData.chapters[chapter.id].numberOfVideos
                      }
                      videoProgress={
                        progressData.chapters[chapter.id].videoProgress
                      }
                      videosWatched={
                        progressData.chapters[chapter.id]
                          .videoProgressVideosWatched
                      }
                    />
                  </BoxTableCell>

                  <BoxTableCell label={messages.tasksAnswers} shrink={true}>
                    <AnswersProgress
                      numberOfAnswers={
                        progressData.chapters[chapter.id].taskAnswers
                      }
                      numberOfTasks={
                        progressData.chapters[chapter.id].numberOfTasks
                      }
                    />
                  </BoxTableCell>

                  <BoxTableCell label={messages.tasksTaskPoints} shrink={true}>
                    <TaskPointsProgress
                      numberOfTasks={
                        progressData.chapters[chapter.id].numberOfTasks
                      }
                      taskMaxPoints={
                        progressData.chapters[chapter.id].taskMaxPoints
                      }
                      taskPoints={progressData.chapters[chapter.id].taskPoints}
                      taskPointsProgress={
                        progressData.chapters[chapter.id].taskPointsProgress
                      }
                    />
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator
            icon="list-ol"
            title={messages.readerErrorNoChapters}
          />
        )}
      </Box>

      <style jsx>{`
        .chapters {
          flex-grow: 1;
        }
        .chapter {
          align-items: center;
          display: flex;
          gap: 15px;
          padding: 5px 0;
        }
        .chapter-title-wrapper {
          flex-grow: 1;
        }
        .chapter-title {
          color: ${theme.textColor};
          font-size: 1.5rem;
          font-weight: 600;
          text-decoration: none;
        }
        .chapter-title-link {
          cursor: pointer;
        }
        .chapter-title-link:focus,
        .chapter-title-link:hover {
          text-decoration: underline;
        }
        .unpublished {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default ProfileCourseChapters;
